import { formatDateString } from "@/v2-common/utils/utils.date"
import ExternalLink from "@/v2-ui/ExternalLink"
import {
  hasProvenance,
  type LicenseProvenanceLike,
} from "@/v2-backoffice-client/license/changes/license.changes.utils"
import {
  GITHUB_COMMIT_URL,
  GITHUB_PULL_REQUEST_URL,
  LINEAR_ISSUE_URL,
} from "@/v2-backoffice-client/license/changes/license.changes.constants"

export type LicenseProvenanceProps = {
  provenance: LicenseProvenanceLike
  className?: string
}

const LINK_CLASS = "whitespace-nowrap underline-offset-2 hover:underline"

// Where a surface came from, in one line: the date its code first appeared,
// the release that shipped it (or "unreleased"), and the Linear issue, PR
// and commit as links — the context behind a capability, read from git by
// the scan. Used in the Changes feed and in the package editor's hover
// cards, so both say it the same way.
function LicenseProvenance(props: LicenseProvenanceProps) {
  const { provenance, className } = props
  const {
    IntroducedAt,
    IntroducedCommit,
    IntroducedIssue,
    IntroducedPullRequest,
    IntroducedVersion,
  } = provenance
  if (!hasProvenance(provenance)) return null
  return (
    <span className={className}>
      {IntroducedAt && <span>{formatDateString(IntroducedAt)}</span>}
      <span className="text-muted-foreground">
        {" · "}
        {IntroducedVersion ?? "unreleased"}
      </span>
      {IntroducedIssue && (
        <>
          {" · "}
          <ExternalLink
            href={`${LINEAR_ISSUE_URL}${IntroducedIssue}`}
            className={LINK_CLASS}
          >
            {IntroducedIssue}
          </ExternalLink>
        </>
      )}
      {IntroducedPullRequest && (
        <>
          {" · "}
          <ExternalLink
            href={`${GITHUB_PULL_REQUEST_URL}${IntroducedPullRequest}`}
            className={LINK_CLASS}
          >
            {`PR #${IntroducedPullRequest}`}
          </ExternalLink>
        </>
      )}
      {IntroducedCommit && (
        <>
          {" · "}
          <ExternalLink
            href={`${GITHUB_COMMIT_URL}${IntroducedCommit}`}
            className={`font-mono text-[11px] ${LINK_CLASS}`}
          >
            {IntroducedCommit}
          </ExternalLink>
        </>
      )}
    </span>
  )
}

export default LicenseProvenance
